import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, CloudOff, RefreshCw } from "lucide-react";
import { APP_EVENTS } from "@/lib/brand";
import { isDesktopApp } from "@/lib/runtime";
import {
  getConnectionState,
  subscribeToConnection,
  type ConnectionState,
} from "@/lib/session";
import { drainOutboxNow, getSyncSnapshot } from "@/lib/desktop-sync";

/**
 * A quiet, truthful account of the outbox.
 *
 * The sync engine has always been silent plumbing; this makes the promise
 * visible. At rest it says one calm line. Offline it counts what is queued so
 * a preacher closing the lid on Saturday night can see, in words, that the
 * writing is on this Mac and will follow. A conflict is the only state
 * allowed to look alarming.
 */
export function SyncStatus() {
  const [connection, setConnection] = useState<ConnectionState>(getConnectionState);
  const [pending, setPending] = useState(0);
  const [conflicts, setConflicts] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(() => {
    void getSyncSnapshot()
      .then(({ pending, conflicts }) => {
        setPending(pending);
        setConflicts(conflicts);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => subscribeToConnection(setConnection), []);

  useEffect(() => {
    refresh();
    window.addEventListener(APP_EVENTS.outboxChanged, refresh);
    window.addEventListener(APP_EVENTS.documentSynced, refresh);
    window.addEventListener(APP_EVENTS.syncConflict, refresh);
    // Events cover the app's own writes; the interval covers everything else
    // (another window, a cleared conflict) without being chatty.
    const interval = setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener(APP_EVENTS.outboxChanged, refresh);
      window.removeEventListener(APP_EVENTS.documentSynced, refresh);
      window.removeEventListener(APP_EVENTS.syncConflict, refresh);
      clearInterval(interval);
    };
  }, [refresh]);

  const syncNow = useCallback(() => {
    setSyncing(true);
    void drainOutboxNow()
      .catch(() => undefined)
      .finally(() => {
        setSyncing(false);
        refresh();
      });
  }, [refresh]);

  if (!isDesktopApp) return null;

  const offline = connection === "offline";

  let icon: React.ReactNode;
  let label: string;
  let tone = "text-muted-foreground";

  if (conflicts > 0) {
    icon = <AlertTriangle className="h-3.5 w-3.5 shrink-0" />;
    label = conflicts === 1 ? "1 change needs review" : `${conflicts} changes need review`;
    tone = "text-destructive";
  } else if (offline && pending > 0) {
    icon = <CloudOff className="h-3.5 w-3.5 shrink-0" />;
    label = pending === 1 ? "Offline — 1 change queued" : `Offline — ${pending} changes queued`;
  } else if (offline) {
    icon = <CloudOff className="h-3.5 w-3.5 shrink-0" />;
    label = "Offline — working locally";
  } else if (syncing || pending > 0) {
    icon = <RefreshCw className={`h-3.5 w-3.5 shrink-0 ${syncing ? "animate-spin" : ""}`} />;
    label = pending > 0 ? `Syncing ${pending}…` : "Syncing…";
  } else {
    icon = <Check className="h-3.5 w-3.5 shrink-0" />;
    label = "All changes synced";
  }

  const canSyncNow = !offline && pending > 0 && !syncing;

  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 text-xs ${tone}`}
      role="status"
      aria-live="polite"
    >
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      {canSyncNow && (
        <button
          type="button"
          onClick={syncNow}
          className="ml-auto shrink-0 underline underline-offset-2 hover:text-foreground"
        >
          Sync now
        </button>
      )}
    </div>
  );
}
